import { IAddToCartModal } from '@models/IAddToCartModal';
import { IAddToCartTray } from '@models/IAddToCartTray';
import { ILink } from '@models/ILink';
import { IMessage } from '@models/IMessage';
import { IProductTrackingData } from '@models/IProductTrackingData';
import AddToCartErrorEnum from '@models/enums/AddToCartErrorEnum';
import InfoTypeEnum from '@models/enums/InfoTypeEnum';
import { addItemToCart } from '@services/cart';
import useCartSizeStore from '@state/CartSizeStore';
import updateDataLayer from '@utils/helpers/gtm';
import mapToNewTrackingData from '@utils/helpers/mapToNewTrackingData';
import { AxiosError } from 'axios';
import Cookies from 'js-cookie';
import { useState } from 'react';

interface Props {
  readonly addToCartAddedLabel: string;
  readonly addToCartErrorLabel: string;
  readonly basketEndpoint: string;
  readonly basketLink: ILink;
  readonly code: string;
  readonly isCommerceEnabled?: boolean;
  readonly maxBasketQuantityText: IMessage;
  readonly maxBasketValueText: IMessage;
  readonly maxProductQuantityText: IMessage;
  readonly mixedCartText: IMessage;
  readonly skuCode: string;
  readonly trackingData?: IProductTrackingData;
}

const useProductDetailsAddToCart = ({
  addToCartAddedLabel,
  addToCartErrorLabel,
  basketEndpoint,
  basketLink,
  code,
  isCommerceEnabled,
  maxBasketQuantityText,
  maxBasketValueText,
  maxProductQuantityText,
  mixedCartText,
  skuCode,
  trackingData,
}: Props) => {
  const [isAddToCartModalOpen, setIsAddToCartModalOpen] = useState(false);
  const [isAddToCartTrayOpen, setIsAddToCartTrayOpen] = useState(false);
  const [addToCartTrayPayload, setAddToCartTrayPayload] =
    useState<IAddToCartTray>();
  const [addToCartModalContent, setAddToCartModalContent] =
    useState<IAddToCartModal>({
      title: '',
      description: '',
      type: InfoTypeEnum.Information,
    });
  const [isAddToCartLoading, setIsAddToCartLoading] = useState(false);
  const [isAddingToCartFromStickyBuyBar, setIsAddingToCartFromStickyBuyBar] =
    useState(false);

  const { setCartSize, cartSizeName } = useCartSizeStore((state) => ({
    cartSizeName: state.cartSizeName,
    setCartSize: state.setCartSize,
  }));

  const handleAddToCartModal = (content: IAddToCartModal) => {
    setAddToCartModalContent(content);
    setIsAddToCartModalOpen(true);
  };

  const handleAddToCartError = (error: AxiosError) => {
    const errorCode = error.response?.data?.errorCode;

    if (errorCode === AddToCartErrorEnum.mixedCart) {
      handleAddToCartModal({
        title: mixedCartText.title,
        description: mixedCartText.text,
        type: InfoTypeEnum.Error,
      });

      return;
    }

    if (errorCode === AddToCartErrorEnum.maxProductQuantity) {
      handleAddToCartModal({
        title: maxProductQuantityText.title,
        description: maxProductQuantityText.text,
        type: InfoTypeEnum.Error,
        ...(isCommerceEnabled ? { ctaLink: basketLink } : {}),
      });

      return;
    }

    if (errorCode === AddToCartErrorEnum.maxBasketQuantity) {
      handleAddToCartModal({
        title: maxBasketQuantityText.title,
        description: maxBasketQuantityText.text,
        type: InfoTypeEnum.Error,
        ctaLink: basketLink,
      });

      return;
    }

    if (errorCode === AddToCartErrorEnum.maxBasketValue) {
      handleAddToCartModal({
        title: maxBasketValueText.title,
        description: maxBasketValueText.text,
        type: InfoTypeEnum.Error,
        ctaLink: basketLink,
      });

      return;
    }

    handleAddToCartModal({
      title: addToCartErrorLabel,
      type: InfoTypeEnum.Error,
    });
  };

  const handleAddProductToCart = async (isFromStickyBuyBar: boolean) => {
    setIsAddingToCartFromStickyBuyBar(isFromStickyBuyBar);
    setIsAddToCartLoading(true);

    try {
      const addToCartTrayPayloadResponse = await addItemToCart(basketEndpoint, {
        code: isCommerceEnabled ? code : skuCode,
        quantity: 1,
      });

      if (isCommerceEnabled) {
        setAddToCartTrayPayload(addToCartTrayPayloadResponse);
        setIsAddToCartTrayOpen(true);
      } else {
        handleAddToCartModal({
          title: addToCartAddedLabel,
          type: InfoTypeEnum.Success,
        });
      }

      setIsAddToCartLoading(false);
      setCartSize(Number(Cookies.get(cartSizeName)) || 0);

      const commerceTrackingData = trackingData
        ? mapToNewTrackingData(trackingData, { hideIndex: true })
        : null;
      if (commerceTrackingData) {
        const updatedCommerceTrackingData = {
          ...commerceTrackingData,
          ...(isCommerceEnabled
            ? {
                quantity:
                  addToCartTrayPayloadResponse?.addedItem.selectedQuantity,
              }
            : {}),
        };

        updateDataLayer({
          event: 'addToCart',
          ...(isFromStickyBuyBar ? { content_type: 'sticky_buy_bar' } : {}),
          ecommerce: {
            currency: trackingData?.currency,
            items: [updatedCommerceTrackingData],
            ...(isCommerceEnabled
              ? { value: addToCartTrayPayloadResponse?.totalBasketValue }
              : {}),
          },
        });
      }
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: Allowed during error reporting
      console.log({ error });

      setIsAddToCartLoading(false);
      handleAddToCartError(error as AxiosError);
    }
  };

  return {
    isAddingToCartFromStickyBuyBar,
    isAddToCartLoading,
    isAddToCartTrayOpen,
    addToCartTrayPayload,
    isAddToCartModalOpen,
    addToCartModalContent,
    setIsAddToCartTrayOpen,
    setIsAddToCartModalOpen,
    handleAddProductToCart,
  };
};

export default useProductDetailsAddToCart;
